import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { getSupabaseClient } from '../services/supabase';
import { useDeduplicatedPostHog } from '../utils/posthogWrapper';
import { OAUTH_RETURN_KEY } from '../utils/constants';
import { isSignupRecoveryDone, markSignupRecoveryDone } from '../utils/signupRecovery';

export type OAuthProvider = 'google' | 'apple' | 'discord' | 'twitter' | 'github';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAuthenticated: boolean;
  signInWithOAuth: (provider: OAuthProvider) => Promise<void>;
  signOut: () => Promise<void>;
  getAccessToken: () => Promise<string | undefined>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Component to handle PostHog identify when user changes
function PostHogIdentify({ user }: { user: User | null }) {
  const posthog = useDeduplicatedPostHog();
  const identifiedUserIdRef = useRef<string | null>(null);
  const lastPropertiesHashRef = useRef<string | null>(null);

  useEffect(() => {
    if (!posthog) return;

    if (user && user.email && user.id !== identifiedUserIdRef.current) {
      const userProperties: Record<string, any> = {
        email: user.email,
        user_id: user.id,
        external_id: user.id,
      };

      if (user.user_metadata?.full_name) {
        userProperties.name = user.user_metadata.full_name;
      }
      if (user.app_metadata?.provider) {
        userProperties.auth_provider = user.app_metadata.provider;
      }
      if (user.created_at) {
        userProperties.created_at = user.created_at;
      }

      const propertiesHash = JSON.stringify(userProperties);

      if (propertiesHash !== lastPropertiesHashRef.current) {
        posthog.identify(user.id, userProperties);
        identifiedUserIdRef.current = user.id;
        lastPropertiesHashRef.current = propertiesHash;
      }
    } else if (!user && identifiedUserIdRef.current) {
      posthog.reset();
      identifiedUserIdRef.current = null;
      lastPropertiesHashRef.current = null;
    }
  }, [user, posthog]);

  return null;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let subscription: { unsubscribe: () => void } | null = null;

    const initializeAuth = async () => {
      try {
        const supabase = getSupabaseClient();

        // Get initial session from Supabase
        const { data: { session } } = await supabase.auth.getSession();

        if (mounted) {
          setSession(session);
          setUser(session?.user ?? null);
          setLoading(false);
        }

        // Listen for auth state changes (session updates, sign out, etc.)
        // Note: Login/signup event tracking is handled by AuthCallbackPage
        // which has access to the signup_bonus_granted API response
        const {
          data: { subscription: authSubscription },
        } = supabase.auth.onAuthStateChange((_event, session) => {
          if (mounted) {
            setSession(session);
            setUser(session?.user ?? null);
            setLoading(false);
          }
        });

        subscription = authSubscription;
      } catch (error) {
        if (mounted) {
          setUser(null);
          setSession(null);
          setLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, []);

  // [Mitigation B] Recovery: if a Supabase session exists on mount but no
  // signupUser flag is set for this user, fire signupUser. Catches users
  // who authenticated through paths where AuthCallbackPage / OtpModal
  // didn't run to completion (e.g. FB/Instagram in-app browser → Safari
  // handoff, native-context dropouts). Idempotent on backend; flag
  // prevents re-fire across tabs/sessions.
  //
  // Skipped on /auth/callback — AuthCallbackPage owns that page and sets
  // the flag itself on success. 3s delay lets OtpModal's own signupUser
  // call (which can fire concurrently) complete and set the flag first,
  // avoiding a race that could swallow the welcome-toast signup_bonus_granted
  // response from the legitimate path.
  useEffect(() => {
    if (loading) return;
    if (!session?.user?.id || !session.user.email) return;
    if (typeof window !== 'undefined' && window.location.pathname === '/auth/callback') return;
    if (isSignupRecoveryDone(session.user.id)) return;

    const timer = setTimeout(async () => {
      if (!session.user?.id || !session.user.email) return;
      if (isSignupRecoveryDone(session.user.id)) return; // re-check; OtpModal/AuthCallbackPage may have just set it
      try {
        const { signupUser } = await import('../services/credits');
        await signupUser(
          session.user.email!,
          session.user.id,
          session.user.user_metadata?.full_name
        );
        markSignupRecoveryDone(session.user.id);
      } catch {
        // Swallow. Backend rescue path (GET /api/user fallback) is the
        // independent safety net, and the next mount will retry anyway.
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [loading, session]);

  // Clean up OAuth hash from URL on mount and visibility change
  useEffect(() => {
    const cleanupOAuthHash = () => {
      if (typeof window !== 'undefined') {
        const hash = window.location.hash;
        if (hash.includes('access_token=') || hash.includes('error=')) {
          const hashParams = new URLSearchParams(hash.substring(1));
          const error = hashParams.get('error');
          const userAuthenticated = !!user;

          // Don't clean on /auth/callback - let AuthCallbackPage handle it
          const isOnCallbackPage = window.location.pathname === '/auth/callback';
          const oauthPending = !!localStorage.getItem(OAUTH_RETURN_KEY);
          const shouldClean =
            !isOnCallbackPage &&
            (error || (userAuthenticated && hash.includes('access_token=') && !oauthPending));

          if (shouldClean) {
            const cleanUrl = window.location.origin + window.location.pathname + window.location.search;
            window.history.replaceState(null, '', cleanUrl);
          }
        }
      }
    };

    cleanupOAuthHash();

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        cleanupOAuthHash();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user]);

  const signInWithOAuth = async (provider: OAuthProvider) => {
    const supabase = getSupabaseClient();
    const currentPath = window.location.pathname;
    const returnPath = currentPath !== '/' && currentPath !== '/auth/callback' ? currentPath : '/';

    // Store return path in localStorage (single source of truth)
    localStorage.setItem(OAUTH_RETURN_KEY, returnPath);

    const callbackUrl = `${window.location.origin}/auth/callback`;

    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: callbackUrl,
      },
    });

    if (error) {
      localStorage.removeItem(OAUTH_RETURN_KEY);
      throw error;
    }
  };

  const signOut = async () => {
    const supabase = getSupabaseClient();
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const getAccessToken = async (): Promise<string | undefined> => {
    const supabase = getSupabaseClient();
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return session?.access_token;
  };

  const value: AuthContextType = {
    user,
    session,
    loading,
    isAuthenticated: !!user,
    signInWithOAuth,
    signOut,
    getAccessToken,
  };

  return (
    <AuthContext.Provider value={value}>
      <PostHogIdentify user={user} />
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
